<script lang="ts" setup>
import { usePermissions } from '@/composables/permissions';
import { PolicyAction } from '@/types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Project } from '@/gql/graphql';
import {
    VBtn,
    VMenu,
    VList,
    VListItem,
    VListItemTitle,
    VDefaultsProvider,
} from 'vuetify/components';

const { hasProjectPermission } = usePermissions();

const { t } = useI18n({
    messages: {
        nl: {
            pending: 'Te voltooien',
            finished: 'Afgerond',
            bottleneck: 'Bottleneck',
            escalation: 'Escalatie',
        },
        en: {
            pending: 'Pending',
            finished: 'Finished',
            bottleneck: 'Bottleneck',
            escalation: 'Escalation',
        },
    },
});

export type DeliverableStatus =
    | 'normal'
    | 'pending'
    | 'finished'
    | 'bottleneck'
    | 'escalation';

type DeliverableStatusMenuProps = {
    project: Pick<
        Project,
        'id' | 'masterPortfolioId' | 'parentProjectId' | 'name'
    >;
    currentStatus: DeliverableStatus;
    label?: string;
    size?: string;
    loading?: boolean;
};

const props = withDefaults(defineProps<DeliverableStatusMenuProps>(), {
    label: undefined,
    size: undefined,
    loading: false,
});

const canUpdateProject = computed(() => {
    return (
        props.project &&
        (hasProjectPermission(PolicyAction.DELIVERABLE_WRITE, props.project) ||
            hasProjectPermission(PolicyAction.PROJECT_WRITE, props.project))
    );
});
const canEscalate = computed(() => {
    return (
        props.project &&
        hasProjectPermission(PolicyAction.ESCALATION_CREATE, props.project)
    );
});
const items = computed<
    {
        title: string;
        value: 'pending' | 'finished' | 'bottleneck' | 'escalation';
        disabled: boolean;
    }[]
>(() => [
    {
        title: t('pending'),
        value: 'pending',
        disabled: !canUpdateProject.value,
    },
    {
        title: t('finished'),
        value: 'finished',
        disabled: !canUpdateProject.value,
    },
    {
        title: t('bottleneck'),
        value: 'bottleneck',
        disabled: !canUpdateProject.value,
    },
    {
        title: t('escalation'),
        value: 'escalation',
        disabled: !canEscalate.value,
    },
]);
const emit = defineEmits<{
    select: [status: DeliverableStatus];
}>();

function handleSelect(value: DeliverableStatus) {
    emit('select', value);
}
</script>
<template>
    <v-menu>
        <template #activator="{ props: templateProps }">
            <VDefaultsProvider
                :defaults="{
                    VBtn: {
                        style: {
                            textTransform: 'none',
                            fontFamily: 'inherit',
                        },
                        class: ['px-0', 'text-none', 'text-label-large'],
                    },
                }"
            >
                <VBtn
                    append-icon="$expand"
                    v-bind="templateProps"
                    color="primary-0"
                    variant="plain"
                    :size
                    :loading
                >
                    <template v-if="label">{{ t(label) }}</template>
                </VBtn>
            </VDefaultsProvider>
        </template>
        <v-list>
            <v-list-item
                v-for="item in items"
                :key="item.title"
                :value="item.value"
                :disabled="item.disabled || (item.value === currentStatus && item.value !== 'bottleneck')"
                @click="handleSelect(item.value)"
            >
                <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>
